import { useEffect, useState } from "react";
import { api, apiError } from "../../lib/api";
import { PageSkeleton, KpiCard, EmptyState, StatusBadge, Avatar } from "../../components/fields";
import ClassroomLogSheet from "../../components/ClassroomLogSheet";
import QuickLogSheet from "../../components/QuickLogSheet";
import BehaviorSheet from "../../components/BehaviorSheet";
import GoalCheckSheet from "../../components/GoalCheckSheet";
import { useBranding } from "../../context/BrandingContext";
import { CheckCircle2, Circle, Users, CalendarCheck, Zap, AlertTriangle, PlusCircle, Target } from "lucide-react";
import { toast } from "sonner";

const greeting = () => { const h = new Date().getHours(); return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening"; };

const BehaviorCounters = ({ student, onTap }) => {
  const [counts, setCounts] = useState({});
  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    api.get(`/behavior-events?student_id=${student.id}&start=${today}&end=${today}`)
      .then((r) => setCounts(r.data.daily_totals[today] || {})).catch(() => {});
  }, [student.id]);
  if (!student.target_behaviors?.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {student.target_behaviors.map((b) => (
        <button key={b} data-testid={`behavior-counter-${student.id}-${b.toLowerCase().replace(/\s+/g, "-")}`}
          onClick={async () => {
            try {
              const r = await api.post("/behavior-events", { student_id: student.id, behavior: b });
              setCounts((c) => ({ ...c, [b]: r.data.today_count }));
              toast.success(`${b}: ${r.data.today_count} today`);
            } catch (e) { toast.error(apiError(e)); }
          }}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors duration-150 active:scale-95">
          <PlusCircle size={12} /> {b} <span className="bg-white rounded-full min-w-[16px] px-1 text-center">{counts[b] || 0}</span>
        </button>
      ))}
    </div>
  );
};

export default function ClassroomToday({ user }) {
  const [data, setData] = useState(null);
  const [logSheet, setLogSheet] = useState(null);
  const [quickLog, setQuickLog] = useState(null);
  const [behaviorSheet, setBehaviorSheet] = useState(null);
  const [goalSheet, setGoalSheet] = useState(null);
  const { branding } = useBranding();

  const load = () => api.get("/aide/classroom-today").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  if (!data) return <PageSkeleton />;
  const firstName = (user?.name || "").split(" ")[0];
  const room = data.classrooms[0];

  return (
    <div className="space-y-5 stagger">
      <div className="fade-up">
        <h1 className="font-heading text-[28px] sm:text-[32px] font-bold tracking-tight text-[#132238] leading-tight">{greeting()}, {firstName}</h1>
        <p className="text-sm text-[#667085] mt-1">{room ? `${room.classroom.name} · ${room.students.length} student${room.students.length === 1 ? "" : "s"}` : "Here's your classroom today."}</p>
      </div>

      {!room ? (
        <div className="bg-white border border-[#E5E9EE] rounded-2xl">
          <EmptyState icon={Users} title="No classroom assigned yet" hint="When your coordinator assigns you a classroom, the routine blocks and roster appear here." />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
            <KpiCard testId="cls-kpi-blocks" label="Routine Blocks" value={room.blocks.length} sub={`${room.blocks.filter((b) => b.logged).length} logged`} icon={CalendarCheck} tone="green" />
            <KpiCard testId="cls-kpi-students" label="Students" value={room.students.length} icon={Users} tone="blue" sub={room.classroom.name} />
            <KpiCard testId="cls-kpi-progress" label="Today" value={`${room.blocks.length ? Math.round(room.blocks.filter((b) => b.logged).length / room.blocks.length * 100) : 0}%`} ring={room.blocks.length ? (room.blocks.filter((b) => b.logged).length / room.blocks.length) * 100 : 0} sub="Documentation" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
            <div className="lg:col-span-2 bg-white border border-[#E5E9EE] rounded-2xl shadow-[0_1px_2px_rgba(15,31,56,0.04)] overflow-hidden">
              <div className="px-5 py-4 border-b border-[#E5E9EE]">
                <h2 className="font-heading text-[17px] font-semibold text-[#132238]">Today's Routine</h2>
                <p className="text-xs text-[#667085] mt-0.5">One log per block for the whole room — add student notes only when needed</p>
              </div>
              <ul className="px-5 py-3">
                {room.blocks.map((b, i) => (
                  <li key={b.block_id} className="relative flex items-center gap-3.5 py-3">
                    {i < room.blocks.length - 1 && <span className="absolute left-[9px] top-[38px] bottom-[-10px] w-px bg-[#E5E9EE]" />}
                    {b.logged ? <CheckCircle2 size={20} className="text-[#087F5B] shrink-0 relative z-10 bg-white" />
                      : <Circle size={20} className="text-slate-300 shrink-0 relative z-10 bg-white" />}
                    <div className="flex-1 flex items-center gap-3 min-w-0">
                      <div className="w-[72px] shrink-0">
                        <p className="text-[13px] font-semibold text-[#132238] font-mono">{b.start}</p>
                        <p className="text-[11px] text-[#98A2B3] font-mono">{b.end}</p>
                      </div>
                      <p className="flex-1 text-sm font-medium text-[#132238] truncate">{b.name}</p>
                      {b.logged ? (
                        <div className="flex items-center gap-2">
                          <StatusBadge tone="success">Logged</StatusBadge>
                          <button data-testid={`log-block-btn-${b.block_id}`} onClick={() => setLogSheet(b)}
                            className="text-xs font-semibold text-[#667085] hover:text-[#132238] px-2 py-1.5 rounded-lg hover:bg-slate-50 transition-colors duration-150">Edit</button>
                        </div>
                      ) : (
                        <button data-testid={`log-block-btn-${b.block_id}`} onClick={() => setLogSheet(b)}
                          className="text-xs font-semibold bg-[#087F5B] text-white hover:bg-[#066548] px-3.5 py-2 rounded-[10px] transition-colors duration-150 active:scale-95">Log Block</button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-4">
              <div className="bg-white border border-[#E5E9EE] rounded-2xl p-5 shadow-[0_1px_2px_rgba(15,31,56,0.04)]">
                <h3 className="font-heading text-[15px] font-semibold text-[#132238] mb-3">Roster — quick actions</h3>
                <div className="space-y-3">
                  {room.students.map((s) => (
                    <div key={s.id} data-testid={`roster-student-${s.id}`} className="border border-[#E5E9EE] rounded-xl p-3">
                      <div className="flex items-center gap-2.5">
                        <Avatar name={s.name} size="sm" tone="mint" />
                        <p className="flex-1 text-sm font-medium text-[#132238] truncate">{s.name}</p>
                        <button data-testid={`quick-log-btn-${s.id}`} onClick={() => setQuickLog(s)} aria-label="Quick log"
                          className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-semibold text-[#087F5B] bg-[#EAF8F3] hover:bg-[#d9f2e8] transition-colors duration-150"><Zap size={13} /> Quick</button>
                        <button data-testid={`behavior-btn-${s.id}`} onClick={() => setBehaviorSheet({ id: s.id, first_name: s.name.split(" ")[0], last_name: s.name.split(" ").slice(1).join(" ") })} aria-label="Behavior / concern"
                          className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-semibold text-amber-600 bg-amber-50 hover:bg-amber-100 transition-colors duration-150"><AlertTriangle size={13} /> Behavior</button>
                        {branding?.goal_check_enabled && (
                          <button data-testid={`goal-check-btn-${s.id}`} onClick={() => setGoalSheet({ id: s.id, first_name: s.name.split(" ")[0], name: s.name })} aria-label="Goal check"
                            className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-semibold text-[#087F5B] bg-[#EAF8F3] hover:bg-[#d9f2e8] transition-colors duration-150"><Target size={13} /> Goals</button>
                        )}
                      </div>
                      <BehaviorCounters student={s} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {logSheet && room && (
        <ClassroomLogSheet classroom={room.classroom} block={logSheet} students={room.students}
          onClose={() => setLogSheet(null)} onSaved={() => { setLogSheet(null); load(); }} />
      )}
      {quickLog && <QuickLogSheet student={{ id: quickLog.id, name: quickLog.name, first_name: quickLog.name.split(" ")[0] }} onClose={() => setQuickLog(null)} />}
      {behaviorSheet && <BehaviorSheet student={behaviorSheet} onClose={() => setBehaviorSheet(null)} onSaved={() => setBehaviorSheet(null)} />}
      {goalSheet && <GoalCheckSheet student={goalSheet} onClose={() => setGoalSheet(null)} />}
    </div>
  );
}
